/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LEVELS, DataTable, DataTableCell, columnTypes, innings, ratio, slg } from 'yankees-react-utils';
import { pitchFXERA, hitFXERA, RVPerN } from '../../../utils/stats';
import { getCellShadingClass } from '../../../utils/formatters';
import { getLevelAvgAndStDev } from '../../../utils/helpers';
import usePlayerSettings from '../../../utils/usePlayerSettings';
import StatTooltip from '../../../components/StatTooltip';
import VideoPlaylistButton from '../../../components/VideoPlaylistButton';
import VideoPlayerButton from '../../../components/VideoPlayerButton';

const SuperHeaderRow = styled.tr`
    background-color: #C6D3DC;
`;
const SuperHeaderCell = styled.th`
    && {
        border-left: 1px solid #FFF;
        border-right: 1px solid #FFF;
        background-color: #244C74 !important;
        color: #FFF;
    }
`;

const GameStats = ({ data, ppfxAvgs, showMinLgStats, levelId, isLhp, isLive, livePlaylistFn }) => {
    const { t: translate } = useTranslation();
    const [rows, setRows] = useState([]);
    const { ShowGameScore } = usePlayerSettings();
    let yearid = null;
    const splitTitle = { id: 'split', title: '', type: columnTypes.TEXT };
    const playlistcol = { id: 'videoplaylist', title: null, type: columnTypes.NODE };
    const columnsBase = [
        playlistcol,
        { id: 'date', title: translate('common.date'), transform: (row) => (row.gamedate || row.yearid), type: columnTypes.TEXT },
        {
            id: 'opp',
            title: translate('sections.reports.pitcher.opp-lev'),
            type: columnTypes.TEXT,
            transform: ({ gamedate, opp, levelid, overridetitle }) => (overridetitle || (gamedate ? opp : (LEVELS.find(({ id }) => id === levelid)?.abbr || 'MIN+MLB'))),
        },
    ];
    const columns = (distinctKey, showGameScore) => [
        { id: `ip${distinctKey}`, title: translate('baseball.abbr-inning-pitched'), statistic: 'IP', transform: (row) => (row.isLevelAvg || row.outs === null ? '--' : innings(row.outs)), precision: 1 },
        { id: `pa${distinctKey}`, title: translate('sections.reports.pitcher.abbr-tot-batter-faced'), statistic: 'BF', transform: (row) => (row.isLevelAvg ? '--' : row.pa) },
        { id: `pit${distinctKey}`, title: translate('sections.reports.pitcher.abbr-num-pitches'), statistic: 'Pitches', transform: (row) => (row.isLevelAvg ? '--' : row.pitches || 0) },
        { id: `h${distinctKey}`, title: translate('baseball.abbr-hits'), statistic: 'H', transform: (row) => (row.isLevelAvg ? '--' : row.b1 + row.b2 + row.b3 + row.hr) },
        { id: `so${distinctKey}`, title: translate('baseball.abbr-strikeout-so'), statistic: 'SO', transform: (row) => (row.isLevelAvg ? '--' : row.so || 0) },
        { id: `ubb${distinctKey}`, title: translate('baseball.abbr-walk-ubb'), statistic: 'UBB', transform: (row) => (row.isLevelAvg ? '--' : row.ubb || 0) },
        { id: `r${distinctKey}`, title: translate('baseball.abbr-runs'), statistic: 'R', transform: (row) => (row.isLevelAvg ? '--' : row.r || 0) },
        { id: `er${distinctKey}`, title: translate('baseball.abbr-earned-runs'), statistic: 'ER', transform: (row) => (row.isLevelAvg ? '--' : row.er || 0) },
        { id: `hr${distinctKey}`, title: translate('baseball.abbr-homerun'), statistic: 'HR', transform: (row) => (row.isLevelAvg ? '--' : row.hr || 0) },
        // Controlling the Zone
        {
            id: `bbperc${distinctKey}`,
            title: translate('sections.reports.pitcher.abr-walk-pct'),
            statistic: 'BBperAB',
            transform: (row) => (row.isLevelAvg ? row.row : ratio(row.ubb, row.pa)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.ubb, row.pa, row.bb_avg, row.bb_stdev, true)),
        },
        {
            id: `stkperc${distinctKey}`,
            title: translate('sections.reports.pitcher.strike-pct'),
            statistic: 'Stk',
            transform: (row) => (row.isLevelAvg ? row.strike_avg : ratio(row.strikes, (row.pitches - row.iballs - row.pitchouts))),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.strikes, (row.pitches - row.iballs - row.pitchouts), row.strike_avg, row.strike_stdev)),
        },
        {
            id: `kzon${distinctKey}`,
            title: translate('sections.reports.pitcher.zone-pct'),
            statistic: 'KZon',
            transform: (row) => (row.isLevelAvg ? row.inz_avg : ratio(row.inz, (row.inz + row.outz))),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.inz, (row.inz + row.outz), row.inz_avg, row.inz_stdev)),
        },
        {
            id: `1pitstk${distinctKey}`,
            title: translate('sections.reports.pitcher.1p-strike-pct'),
            statistic: '1PitStk',
            transform: (row) => (row.isLevelAvg ? row.firstpitchstrike_avg : ratio(row.firstpstrike, row.firstp)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.firstpstrike, row.firstp, row.firstpitchstrike_avg, row.firstpitchstrike_stdev)),
        },
        {
            id: `wayahead${distinctKey}`,
            statistic: 'WayAhead',
            title: translate('sections.reports.pitcher.way-ahead'),
            transform: (row) => (row.isLevelAvg ? row.wayahead_avg : ratio(row.wayaheadpa, row.pa)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.wayaheadpa, row.pa, row.wayahead_avg, row.wayahead_stdev)),
        },
        // Missing Bats
        {
            id: `kperc${distinctKey}`,
            title: translate('sections.reports.pitcher.abbr-strikeout-pct'),
            statistic: 'KperAB',
            transform: (row) => (row.isLevelAvg ? row.so_avg : ratio(row.so, row.pa)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.so, row.pa, row.so_avg, row.so_stdev)),
        },
        {
            id: `ms${distinctKey}`,
            title: translate('sections.reports.pitcher.abbr-miss'),
            statistic: 'Ms',
            transform: (row) => (row.isLevelAvg ? row.ms_avg : ratio((row.sw - row.cn), row.sw)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass((row.sw - row.cn), row.sw, row.ms_avg, row.ms_stdev)),
        },
        {
            id: `calledkperc${distinctKey}`,
            title: translate('sections.reports.pitcher.called-k-pct'),
            statistic: 'CalledStk',
            transform: (row) => (row.isLevelAvg ? row.calledstrike_avg : ratio(row.calledstk, (row.pitches - row.iballs - row.pitchouts))),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.calledstk, (row.pitches - row.iballs - row.pitchouts), row.calledstrike_avg, row.calledstrike_stdev)),
        },
        {
            id: `ch${distinctKey}`,
            title: translate('sections.reports.pitcher.abbr-ch'),
            statistic: 'Ch',
            transform: (row) => (row.isLevelAvg ? row.ch_avg : ratio(row.ch, row.outz)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.ch, row.outz, row.ch_avg, row.ch_stdev)),
        },
        {
            id: `putawayperc${distinctKey}`,
            statistic: 'PutAwayPct',
            title: translate('sections.reports.pitcher.put-away-pct'),
            transform: (row) => (row.isLevelAvg ? row.putaway_avg : ratio(row.so, row.twostrikepitches)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.so, row.twostrikepitches, row.putaway_avg, row.putaway_stdev)),
        },
        // Limiting Damage
        !isLive && {
            id: `contactquality${distinctKey}`,
            title: translate('baseball.abbr-contactquality'),
            statistic: 'ContactQuality',
            transform: (row) => (row.isLevelAvg ? row.contactquality_avg : RVPerN(row.rvhitfxpitbip, row.biprvdenom, 100)),
            precision: 1,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(RVPerN(row.rvhitfxpitbip, row.biprvdenom, 100), 1, row.contactquality_avg, row.contactquality_stdev, true)),
        },
        {
            id: `gbperc${distinctKey}`,
            title: translate('baseball.abbr-groundball-pct'),
            statistic: 'GBPercent',
            transform: (row) => (row.isLevelAvg ? row.gb_avg : ratio(row.gb, (row.ld + row.gb + row.fb + row.pop))),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.gb, (row.ld + row.gb + row.fb + row.pop), row.gb_avg, row.gb_stdev)),
        },
        {
            id: `slug${distinctKey}`,
            title: translate('sections.reports.pitcher.slug-pct'),
            statistic: 'SLG',
            transform: (row) => (row.isLevelAvg ? row.slg_avg : slg(row.b1, row.b2, row.b3, row.hr, row.pa)),
            type: columnTypes.RATE,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(slg(row.b1, row.b2, row.b3, row.hr, row.pa), 1, row.slg_avg, row.slg_stdev, true)),
        },
        {
            id: `avgexitvelo${distinctKey}`,
            title: translate('sections.reports.pitcher.exit-velo'),
            statistic: 'ExitVelo',
            transform: (row) => (row.isLevelAvg ? row.exitvelo_avg : row.exitvelo),
            precision: 1,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.exitvelo, 1, row.exitvelo_avg, row.exitvelo_stdev, true)),
        },
        !isLive && {
            id: `wellhitperc${distinctKey}`,
            title: translate('sections.reports.pitcher.well-hit-pct'),
            statistic: 'WellHitPercent',
            transform: (row) => (row.isLevelAvg ? row.wellhit_avg : ratio(row.wellhit, row.pawhitfx)),
            type: columnTypes.PERCENT,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(row.wellhit, row.pawhitfx, row.wellhit_avg, row.wellhit_stdev, true)),
        },
        // Overall
        !isLive && showGameScore && {
            id: `gamefxera${distinctKey}`,
            statistic: 'GameFXERA',
            title: translate('sections.reports.pitcher.gamefx-era'),
            transform: (row) => (row.isLevelAvg || !row.gameid ? null : hitFXERA(row.rvgamefx, row.pawrv)),
            precision: 2,
            cellClass: (row) => (row.isLevelAvg || !row.gameid ? '' : getCellShadingClass(hitFXERA(row.rvgamefx, row.pawrv), 1, row.gamefxera_avg, row.gamefxera_stdev, true)),
        },
        !isLive && {
            id: `hitfxera${distinctKey}`,
            statistic: 'HitFXERA',
            title: translate('sections.reports.pitcher.hitfx-era'),
            transform: (row) => (row.isLevelAvg ? row.hitfxera_avg : (row.gameid ? null : hitFXERA(row.rvhitfxpitalt, row.pawrv))),
            precision: 2,
            cellClass: (row) => (row.isLevelAvg || row.gameid ? '' : getCellShadingClass(hitFXERA(row.rvhitfxpitalt, row.pawrv), 1, row.hitfxera_avg, row.hitfxera_stdev, true)),
        },
        !isLive && {
            id: `stuffera${distinctKey}`,
            statistic: 'StuffERA',
            title: translate('sections.reports.pitcher.stuff-era'),
            transform: (row) => (row.isLevelAvg ? row.stuffera_avg : pitchFXERA(row.rvstuff, row.pitchfxwrv)),
            precision: 2,
            cellClass: (row) => (row.isLevelAvg || !row.gameid ? '' : getCellShadingClass(pitchFXERA(row.rvstuff, row.pitchfxwrv), 1, row.stuffera_avg, row.stuffera_stdev, true)),
        },
        !isLive && {
            id: `pfxera${distinctKey}`,
            statistic: 'PitchFXERA',
            title: translate('sections.reports.pitcher.pfx-era'),
            transform: (row) => (row.isLevelAvg ? row.pitchfxera_avg : pitchFXERA(row.rvpitchfx, row.pitchfxwrv)),
            precision: 2,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(pitchFXERA(row.rvpitchfx, row.pitchfxwrv), 1, row.pitchfxera_avg, row.pitchfxera_stdev, true)),
        },
        !isLive && {
            id: 'execera',
            statistic: 'ExecERA',
            title: translate('sections.reports.pitcher.exec-era'),
            transform: (row) => (row.isLevelAvg ? row.stuffera_avg : pitchFXERA(row.rvexecera, row.pwexecera)),
            precision: 2,
            cellClass: (row) => (row.isLevelAvg ? '' : getCellShadingClass(pitchFXERA(row.rvexecera, row.pwexecera), 1, row.execera_avg, row.execera_stdev, true)),
        },
    ].filter((x) => x);

    const { gameline, seasonline, bybatterhand } = data;
    const gamelineWithAvgs = gameline.filter((g) => g.filteredlevelid === null).map((g) => {
        yearid = new Date(g.gamedate).getFullYear();
        const videoparams = { gameid: g.gameid, pitcherid: g.pitcherid };
        const videoplaylistLhh = isLive ? <VideoPlaylistButton playlist={livePlaylistFn({ ...videoparams, islhb: true })} /> : (!window.$IsDugoutApp) ? (<VideoPlayerButton {...videoparams} islhb />) : '-';
        const videoplaylistRhh = isLive ? <VideoPlaylistButton playlist={livePlaylistFn({ ...videoparams, islhb: false })} /> : (!window.$IsDugoutApp) ? (<VideoPlayerButton {...videoparams} islhb={false} />) : '-';
        const { mlbStDev, levelAvg } = getLevelAvgAndStDev(ppfxAvgs, g.levelid, yearid);
        const lhhdrill = bybatterhand.find((x) => x.splittype.includes('LHB')) || {};
        const rhhdrill = bybatterhand.find((x) => x.splittype.includes('RHB')) || {};
        const drilldownColumns = [playlistcol, splitTitle, ...columns('sub', ShowGameScore)];
        const drilldown = [
            <tr key={0} />,
            ...[
                { ...lhhdrill, videoplaylist: videoplaylistLhh, split: `${translate('baseball.abbr-versus')} ${translate('baseball.lhh')}` },
                { ...rhhdrill, videoplaylist: videoplaylistRhh, split: `${translate('baseball.abbr-versus')} ${translate('baseball.rhh')}` },
            ].map((r) => (
                <React.Fragment key={r.split}>
                    <tr />
                    <tr>
                        {drilldownColumns.map((c, i) => <DataTableCell key={`${c.id}-${r.split}`} colSpan={i ? 1 : 3} col={c} row={r} />)}
                    </tr>
                </React.Fragment>
            )),
        ];

        const videoplaylist = isLive ? <VideoPlaylistButton playlist={livePlaylistFn(videoparams)} /> : (!window.$IsDugoutApp) ? (<VideoPlayerButton {...videoparams} />) : '-';

        return { ...mlbStDev, ...levelAvg, drilldown, ...g, videoplaylist };
    });
    const seasonlineWithAvgs = seasonline.filter((s, _idx, self) => {
        if (s.levelid === 8) return true;
        if (showMinLgStats) {
            if (s.levelid) return true;
            return self.filter((x) => !!x.levelid && x.yearid === s.yearid).length > 1;
        }
        return false;
    }).map((s) => {
        const { mlbStDev, levelAvg } = getLevelAvgAndStDev(ppfxAvgs, s.levelid, s.yearid);
        return { ...mlbStDev, ...levelAvg, ...s };
    });

    useEffect(() => {
        const avgRowName = `${isLhp ? translate('baseball.lhp') : translate('baseball.rhp')} ${LEVELS.find(({ id }) => id === levelId)?.abbr} ${translate('common.abbr-average')} ${yearid}`;
        setRows([
            ...gamelineWithAvgs,
            ...seasonlineWithAvgs, {
                ...ppfxAvgs.find((p) => p.yearid === yearid) || {},
                isLevelAvg: true,
                overridetitle: avgRowName,
            }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const superheader = (
        <SuperHeaderRow>
            <SuperHeaderCell colSpan={13}>&nbsp;</SuperHeaderCell>
            <SuperHeaderCell colSpan={5}>{translate('sections.reports.pitcher.controlling-the-zone')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={5}>{translate('sections.reports.pitcher.missing-bats')}</SuperHeaderCell>
            <SuperHeaderCell colSpan={isLive ? 3 : 5}>{translate('sections.reports.pitcher.limiting-damage')}</SuperHeaderCell>
            {!isLive && <SuperHeaderCell colSpan={5}>{translate('sections.reports.pitcher.overall')}</SuperHeaderCell>}
        </SuperHeaderRow>
    );

    return (
        <DataTable
            sortable={false}
            superheader={superheader}
            columns={[...columnsBase, ...columns('table', ShowGameScore)]}
            rows={rows}
            statTooltip={StatTooltip}
        />
    );
};

GameStats.propTypes = {
    data: PropTypes.shape({
        gameline: PropTypes.arrayOf(PropTypes.shape({
            gamedate: PropTypes.string.isRequired,
        })),
        seasonline: PropTypes.arrayOf(PropTypes.shape({
            yearid: PropTypes.number.isRequired,
        })),
        bybatterhand: PropTypes.arrayOf(PropTypes.shape({
            splittype: PropTypes.string.isRequired,
        })),
    }).isRequired,
    ppfxAvgs: PropTypes.arrayOf(PropTypes.object).isRequired,
    showMinLgStats: PropTypes.bool.isRequired,
    isLhp: PropTypes.bool.isRequired,
    levelId: PropTypes.number.isRequired,
    isLive: PropTypes.bool,
    livePlaylistFn: PropTypes.func,
};

GameStats.defaultProps = {
    isLive: false,
    livePlaylistFn: () => {},
};

export default GameStats;
